import { MutableRefObject, useEffect, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useAnimatedBird } from "../../../../layout/product-tour/animated-bird.provider";
import { Tour, TourProps } from "antd";
import { useAuth } from "../../../auth/contexts/auth.provider";
import {
  Authority,
  ProductTourType,
  enableTourVisited,
  useAppLayout,
} from "@dataone/lib";
import { useQuery } from "@tanstack/react-query";

interface TourSmartSearchProps {
  children: React.ReactNode;
  stepRef1: MutableRefObject<null>;
  stepRef2: MutableRefObject<null>;
  stepRef3: MutableRefObject<null>;
  stepRef4: MutableRefObject<null>;
  stepRef5: MutableRefObject<null>;
  stepRef6: MutableRefObject<null>;
  stepRef7: MutableRefObject<null>;
  stepRef8: MutableRefObject<null>;
  setModalOpen: React.Dispatch<React.SetStateAction<boolean>>;
}

const TourSmartSearch = ({
  children,
  stepRef1,
  stepRef2,
  stepRef3,
  stepRef4,
  stepRef5,
  stepRef6,
  stepRef7,
  stepRef8,
  setModalOpen,
}: TourSmartSearchProps) => {
  const [tourOpen, setTourOpen] = useState<boolean>(false);
  const [tourCurrent, setTourCurrent] = useState(0);

  const location = useLocation();

  const { updateBirdPosition, top, right } = useAnimatedBird();
  const [stepBirdPosition, setStepBirdPosition] = useState({
    top: 3,
    right: 30,
  });
  const [isTourHidden, setTourHidden] = useState(true);

  const navigate = useNavigate();
  const productTour = useLocation().state?.productTour;

  const { user, setUser } = useAuth();

  const getStepBirdTopPosition = (ref: React.MutableRefObject<null>) => {
    if (ref.current) {
      const el = (ref.current as HTMLElement).getBoundingClientRect();
      return el.top + el.height - 20;
    }
    return 3;
  };

  const getStepBirdRightPosition = (ref: React.MutableRefObject<null>) => {
    if (ref.current) {
      const el = (ref.current as HTMLElement).getBoundingClientRect();
      return window.innerWidth - (el.right - el.width / 2) - 32;
    }
    return 30;
  };

  useQuery({
    queryFn: () => enableTourVisited(ProductTourType.SMART_SEARCH_TOUR),
    enabled:
      user?.groups[0] === Authority.OG_SUBSCRIBER &&
      !user?.tour?.smartSearchTour &&
      tourOpen,
    onSuccess: () => {
      user &&
        setUser({
          ...user,
          tour: user.tour
            ? {
                ...user.tour,
                smartSearchTour: true,
              }
            : {
                dashboardTour: false,
                searchVisualisationTour: false,
                smartSearchTour: true,
              },
        });
    },
  });

  useEffect(() => {
    if (tourOpen && location.state === null) handleCloseTour();
  }, [location]);

  useEffect(() => {
    if (
      (user?.groups[0] === Authority.OG_SUBSCRIBER &&
        !user?.tour?.smartSearchTour) ||
      productTour === "/dashboard"
    ) {
      navigate("/dashboard", { replace: true, state: {} });
      const stepRight = getStepBirdRightPosition(stepRef1);
      const stepTop = getStepBirdTopPosition(stepRef1);
      setStepBirdPosition({ top: stepTop, right: stepRight });
      updateBirdPosition(stepRight, stepTop);
      setTourOpen(true);
    }
  }, [productTour]);

  const tourSteps: TourProps["steps"] = [
    {
      title: "Hello and welcome to One Gate!",
      description:
        "I'll be your personal guide. Feel free to search, interact with your data, locate what you require from your data sources, and engage in conversations with our AI.",
      mask: false,
      arrow: false,
      className: isTourHidden ? "tour-hidden" : "",
      placement: "bottom",
      target: () => stepRef1.current!,
      onNext: () => {
        setTimeout(() => {
          setTourHidden(true);
          setTourCurrent(1);
          const stepRight = getStepBirdRightPosition(stepRef2);
          const stepTop = getStepBirdTopPosition(stepRef2);
          setStepBirdPosition({ top: stepTop, right: stepRight });
          updateBirdPosition(stepRight, stepTop);
        }, 100);
      },
    },
    {
      title:
        "Speak or type your question to find what you're looking for in your data!",
      mask: false,
      arrow: false,
      className: isTourHidden ? "tour-hidden" : "",
      placement: "bottom",
      target: () => stepRef2.current,
      onNext: () => {
        setTourHidden(true);
        setTourCurrent(2);
        setTimeout(() => {
          const stepRight = getStepBirdRightPosition(stepRef3);
          const stepTop = getStepBirdTopPosition(stepRef3);
          setStepBirdPosition({ top: stepTop, right: stepRight });
          updateBirdPosition(stepRight, stepTop);
        }, 100);
      },
    },
    {
      title: "Ready to dive in?",
      description:
        "Choose the data you want to search through or create a new source for fresh information.",
      mask: false,
      arrow: false,
      className: isTourHidden ? "tour-hidden" : "",
      placement: "bottom",
      target: () => stepRef3.current!,
      onNext: () => {
        setTourHidden(true);
        setTourCurrent(3);
        (stepRef3.current! as HTMLElement)?.click();
        setTimeout(() => {
          const stepRight = getStepBirdRightPosition(stepRef4);
          const stepTop = getStepBirdTopPosition(stepRef4);
          setStepBirdPosition({ top: stepTop, right: stepRight });
          updateBirdPosition(stepRight, stepTop);
        }, 100);
      },
    },
    {
      title: "Data source creation",
      description:
        "Create a new data source by picking the data source type you want.",
      mask: false,
      arrow: false,
      className: isTourHidden ? "tour-hidden" : "",
      placement: "bottom",
      target: () => stepRef4.current!,
      onNext: () => {
        setModalOpen(false);
        setTourHidden(true);
        setTourCurrent(4);
        setTimeout(() => {
          const stepRight = getStepBirdRightPosition(stepRef5);
          const stepTop = getStepBirdTopPosition(stepRef5);
          setStepBirdPosition({ top: stepTop, right: stepRight });
          updateBirdPosition(stepRight, stepTop);
        }, 100);
      },
    },
    {
      title: "Chat with your data source(s).",
      description:
        "Have a focused conversation based on your own created data source.",
      mask: false,
      arrow: false,
      className: isTourHidden ? "tour-hidden" : "",
      placement: "bottom",
      target: () => stepRef5.current!,
      onNext: () => {
        setTourHidden(true);
        (stepRef5.current! as HTMLElement)?.click();
        setTimeout(() => {
          setTourCurrent(5);
          const stepRight = getStepBirdRightPosition(stepRef6);
          const stepTop = getStepBirdTopPosition(stepRef6) - 160;
          setStepBirdPosition({ top: stepTop, right: stepRight });
          updateBirdPosition(stepRight, stepTop);
        }, 100);
      },
    },
    {
      title: "Select your data source(s)",
      mask: false,
      arrow: false,
      className: isTourHidden ? "tour-hidden" : "",
      placement: "top",
      target: () => stepRef6.current!,
      onNext: () => {
        setTourHidden(true);
        setTourCurrent(6);
        (stepRef6.current! as HTMLElement)?.click();
        setTimeout(() => {
          const stepRight = getStepBirdRightPosition(stepRef7);
          const stepTop = getStepBirdTopPosition(stepRef7);
          setStepBirdPosition({ top: stepTop, right: stepRight });
          updateBirdPosition(stepRight, stepTop);
        }, 100);
      },
    },
    {
      title: "Start chatting",
      mask: false,
      arrow: false,
      className: isTourHidden ? "tour-hidden" : "",
      placement: "bottom",
      target: () => stepRef7.current!,
      onClose: () => {
        handleCloseTour();
        (stepRef8.current! as HTMLElement)?.click();
      },
      onFinish: () => {
        handleCloseTour();
        (stepRef8.current! as HTMLElement)?.click();
      },
    },
  ];

  const handleCloseTour = () => {
    setTourOpen(false);
    updateBirdPosition(30, 3);
    setTourCurrent(0);
  };

  useEffect(() => {
    if (
      Math.sqrt(
        Math.pow(stepBirdPosition.right - right, 2) +
          Math.pow(stepBirdPosition.top - top, 2)
      ) < 3
    )
      setTourHidden(false);
  }, [top, right, stepBirdPosition.top, stepBirdPosition.right]);

  const containerRef = useRef(null);

  const { collapsed } = useAppLayout();

  useEffect(() => {
    const resizeObserver = new ResizeObserver((entries) => {
      if (entries[0].contentRect.width < (collapsed ? 800 : 650))
        handleCloseTour();
    });

    containerRef.current && resizeObserver.observe(containerRef.current);

    return () => {
      resizeObserver.disconnect();
    };
  }, []);

  return (
    <>
      {children}
      <span
        ref={containerRef}
        style={{ position: "absolute", width: "100%" }}
      />
      <Tour
        disabledInteraction
        current={tourCurrent}
        open={tourOpen}
        onClose={handleCloseTour}
        steps={tourSteps}
      />
    </>
  );
};

export default TourSmartSearch;
